import { useCallback, useRef, useState } from 'react';
import { useResendWithNewEmailV2Mutation } from '../../api/resendWithNewEmail.graphql.generated';
import type { ResendOutcome } from '../../MessagesV2.types';

/**
 * At most this many resends in flight at once. Matches the batch size the bulk recovery script
 * uses (`script/utility/bulkResendEmails.ts` in email_service) rather than firing a whole
 * selection at the provider simultaneously.
 */
const MAX_IN_FLIGHT = 5;

/**
 * One batch, start to finish. A single value rather than an `isSending`/`hasRun` pair, which could
 * express a batch that had not started yet but was somehow in flight.
 */
export type BulkResendStatus = 'idle' | 'sending' | 'complete';

export type BulkResendState = Readonly<{
  /** Per-row outcome, keyed by message id. Absent means not attempted. */
  outcomes: ReadonlyMap<string, ResendOutcome>;
  status: BulkResendStatus;
  /**
   * Ids the backend confirmed. The caller unticks exactly these and leaves the rest selected, so
   * the rows that still need sending are the ones still ticked.
   */
  sentIds: readonly string[];
  sentCount: number;
  /** Backend returned false — a FAILED resend row exists and carries the reason. */
  failedCount: number;
  /** The call rejected — no resend row was written, so a retry is safe. */
  erroredCount: number;
  /** Everything that did not send, however it failed. Drives the headline count. */
  notSentCount: number;
}>;

/**
 * Resends a selection one row at a time.
 *
 * One mutation call per row is deliberate: the mutation returns a bare `Boolean!` with no row id,
 * so calling it per row is what makes an outcome attributable to a specific message. A single bulk
 * call would tell us "2 of 3 worked" without saying which.
 *
 * Never throws. A row that does not send keeps its place in `outcomes` either way — as `failed`
 * when the backend returned false, or `errored` when the call itself rejected — so the operator
 * can see which rows they were and whether a retry is safe.
 */
export const useBulkResend = () => {
  const [resendMutation] = useResendWithNewEmailV2Mutation();
  const [outcomes, setOutcomes] = useState<ReadonlyMap<string, ResendOutcome>>(() => new Map());
  const [status, setStatus] = useState<BulkResendStatus>('idle');
  /**
   * Guards against a second batch. `status` cannot do it: the Resend button disables off state, so
   * two clicks dispatched before React commits the first one would both reach `resend` and send
   * every selected row twice. A ref flips synchronously, within the first call.
   */
  const isSendingRef = useRef(false);

  const record = useCallback((id: string, outcome: ResendOutcome) => {
    setOutcomes(previous => new Map(previous).set(id, outcome));
  }, []);

  const resend = useCallback(
    async (ids: readonly string[]) => {
      if (isSendingRef.current) {
        return;
      }
      isSendingRef.current = true;
      setStatus('sending');
      setOutcomes(new Map(ids.map(id => [id, 'pending' as ResendOutcome])));

      const queue = [...ids];
      const worker = async () => {
        for (;;) {
          const id = queue.shift();
          if (id === undefined) return;
          try {
            const result = await resendMutation({ variables: { id } });
            // Derive from the returned boolean, never from "the call didn't throw" — a false
            // result is a real failure that still created a FAILED row.
            record(id, result.data?.resendWithNewEmailV2 ? 'sent' : 'failed');
          } catch {
            // Rejected before the backend wrote anything — distinct from a returned false, which
            // does leave a FAILED row behind. See the ResendOutcome doc comment.
            record(id, 'errored');
          }
        }
      };

      await Promise.all(Array.from({ length: Math.min(MAX_IN_FLIGHT, queue.length) }, worker));
      isSendingRef.current = false;
      setStatus('complete');
    },
    [resendMutation, record],
  );

  const reset = useCallback(() => {
    setOutcomes(new Map());
    setStatus('idle');
    isSendingRef.current = false;
  }, []);

  const entries = [...outcomes.entries()];
  const sentIds = entries.filter(([, outcome]) => outcome === 'sent').map(([id]) => id);
  const failedCount = entries.filter(([, outcome]) => outcome === 'failed').length;
  const erroredCount = entries.filter(([, outcome]) => outcome === 'errored').length;
  const state: BulkResendState = {
    outcomes,
    status,
    sentIds,
    sentCount: sentIds.length,
    failedCount,
    erroredCount,
    notSentCount: failedCount + erroredCount,
  };

  return { ...state, resend, reset };
};
